import {
  Check,
  Edit,
  ExternalLink,
  Loader2,
  Trash2,
  XCircle,
} from 'lucide-react';
import { AliasId } from './AliasModal';
import { useCallback, useMemo, useRef } from 'react';
import { useApi } from 'shared_components/context';
import { useClientId } from 'shared_components/context/client';
import { useDiscographyIds } from '../useDiscographyIds';
import _ from 'lodash';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { SourceIdResolver } from './CertaintyMarker';

const getIdUrl = (platform: string, id: string) => {
  if (!id) return '#';
  switch (platform) {
    case 'discogs':
      return `https://www.discogs.com/artist/${id}`;
    case 'deezer':
      return `https://www.deezer.com/artist/${id}`;
    case 'spotify':
      return `https://open.spotify.com/artist/${id}`;
    case 'musicbrainz':
      return `https://musicbrainz.org/artist/${id}`;
    default:
      return '#';
  }
};

export function IDField({
  item,
  items,
  source,
  allowDelete = false,
  disabled = false,
  onChange,
  onRemove,
}: {
  readonly allowDelete?: boolean;
  readonly disabled?: boolean;
  readonly source: string;
  readonly item: AliasId;
  readonly items: AliasId[];
  readonly onChange: (newValue: AliasId) => void;
  readonly onRemove: (item: AliasId) => void;
}) {
  const { collectedDiscographyIds } = useDiscographyIds();
  const { clientId } = useClientId();
  const { adminApi: api } = useApi();
  const abortController = useRef<AbortController | null>(null);
  const sourceName = `${source.charAt(0).toUpperCase() + source.slice(1)}`;

  const relevantArtist = useMemo(() => {
    return collectedDiscographyIds.find((a) =>
      a.source_ids[source as keyof typeof a.source_ids].some(
        (s) => s.id === item.id
      )
    );
  }, [collectedDiscographyIds, item]);

  const shouldShowDuplicateAliasSourceIDError = useMemo(() => {
    const duplicate = collectedDiscographyIds.find((artist) => {
      return artist.source_ids[source as keyof typeof artist.source_ids].some(
        (id, index) => {
          if (
            id.id === item.id &&
            `${artist.id}-${source}-${index}` !== item.itemId
          ) {
            console.log('Duplicate found', id, item);
            return true;
          }
          return false;
        }
      );
    });

    const foundRemoteDuplicate = Boolean(duplicate);

    if (foundRemoteDuplicate) return true;

    const foundLocalDuplicate = items
      .filter((id) => id.source === source)
      .some((id, index) => {
        if (id.id === item.id && id.itemId !== item.itemId) {
          return true;
        }
        return false;
      });

    return Boolean(foundLocalDuplicate);
  }, [collectedDiscographyIds, item, items]);

  const handleOnEdit = () => {
    onChange({
      ...item,
      editing: true,
      // Store the original values
      originalId: item.id,
      originalArtistName: item.artistName,
    });
  };

  const handleCancelEditing = () => {
    onChange({
      ...item,
      editing: false,
      id: item.originalId ?? item.id,
      artistName: item.originalArtistName ?? item.artistName,
      originalId: undefined,
      originalArtistName: undefined,
    });
  };

  const handleOnChange = (newId: string) => {
    onChange({
      ...item,
      id: newId,
    });
  };

  const handleOnSave = () => {
    if (!clientId) return;

    if (abortController.current) {
      abortController.current.abort();
    }

    onChange({
      ...item,
      validating: true,
      validated: false,
      error: null,
    });

    abortController.current = new AbortController();

    // Validate the new ID
    api
      .searchForSourceSearchForSource(
        {
          userId: clientId,
          source: source,
          sourceId: item.id,
        },
        {
          signal: abortController.current.signal,
        }
      )
      .then((response) => {
        if (response.name === null) {
          onChange({
            ...item,
            validating: false,
            validated: true,
            valid: false,
            approved: false,
            rejected: false,
            error: `${sourceName} ID does not exist`,
          });
        } else {
          onChange({
            ...item,
            editing: false,
            artistName: response.name,
            originalId: undefined,
            originalArtistName: undefined,
            validating: false,
            validated: true,
            valid: false,
            approved: true,
            rejected: false,
            error: null,
          });
        }
      })
      .catch(() => {
        onChange({
          ...item,
          validating: false,
          validated: true,
          valid: false,
          error: `${sourceName} ID does not exist`,
        });
      });
  };

  const handleOnIdResolverChange = (props: {
    valid: boolean;
    approved: boolean;
    rejected: boolean;
  }) => {
    onChange({
      ...item,
      ...props,
    });
  };

  return (
    <div className="tw-mb-1.5 tw-bg-gray-700 tw-rounded-md tw-p-1.5">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-1">
        <div className="tw-text-xs tw-text-gray-300 tw-flex tw-items-center tw-gap-1">
          {/* External link button */}
          {item.id && !item.originalId ? (
            <>
              {relevantArtist ? (
                <SourceIdResolver
                  artist={relevantArtist}
                  source={source as keyof typeof relevantArtist.source_ids}
                  source_id={item.id}
                  onChange={handleOnIdResolverChange}
                />
              ) : (
                <a
                  className="tw-underline"
                  href={getIdUrl(item.source, item.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`View on ${
                    item.source.charAt(0).toUpperCase() + item.source.slice(1)
                  }`}
                >
                  {item.artistName}
                </a>
              )}
            </>
          ) : null}
        </div>
        {!disabled ? (
          <div className="tw-flex tw-gap-1">
            {item.validating ? (
              <div className="tw-p-1 tw-bg-gray-600 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                <Loader2
                  size={14}
                  className="tw-text-westburyBlue tw-animate-spin"
                />
              </div>
            ) : item.validated &&
              !item.error &&
              !item.editing &&
              !item.created_by_system ? (
              <>
                {/* Edit button for validated IDs */}
                <WButton
                  variant="secondary"
                  onClick={handleOnEdit}
                  label={
                    <>
                      <Edit size={14} className="tw-mr-1" />
                      <span className="tw-text-xs">Edit</span>
                    </>
                  }
                />
              </>
            ) : item.originalId && !item.created_by_system ? (
              <>
                {/* Save button */}
                <WButton
                  variant={
                    Boolean(item.error || shouldShowDuplicateAliasSourceIDError)
                      ? 'disabled'
                      : 'primary'
                  }
                  onClick={handleOnSave}
                  disabled={Boolean(
                    item.error || shouldShowDuplicateAliasSourceIDError
                  )}
                  label={
                    <>
                      <Check size={14} className="tw-mr-1" />
                      <span className="tw-text-xs">Save</span>
                    </>
                  }
                />

                {/* Cancel button */}
                <WButton
                  variant="secondary"
                  onClick={handleCancelEditing}
                  label={
                    <>
                      <XCircle size={14} className="tw-mr-1" />
                      <span className="tw-text-xs">Cancel</span>
                    </>
                  }
                />
              </>
            ) : !item.created_by_system ? (
              <WButton
                variant={
                  Boolean(
                    !item.id ||
                      item.error ||
                      shouldShowDuplicateAliasSourceIDError
                  )
                    ? 'disabled'
                    : 'primary'
                }
                onClick={handleOnSave}
                disabled={Boolean(
                  !item.id ||
                    item.error ||
                    shouldShowDuplicateAliasSourceIDError
                )}
                label={
                  <>
                    <Check size={14} className="tw-mr-1" />
                    <span className="tw-text-xs">Save</span>
                  </>
                }
              />
            ) : null}
            {(allowDelete || item.artistName) &&
              !item.editing &&
              !item.created_by_system && (
                <WButton
                  variant="secondary"
                  disabled={
                    !item.id &&
                    items.filter((i) => i.source === source).length === 1
                  }
                  onClick={() => onRemove(item)}
                  label={<Trash2 size={14} />}
                />
              )}
          </div>
        ) : null}
      </div>
      <div className="tw-flex tw-items-center tw-gap-1">
        <div className="tw-relative tw-flex-grow">
          <input
            type="text"
            disabled={disabled}
            name={`ids.${item.itemId}.id`}
            value={item.id}
            onChange={(e) => handleOnChange(e.target.value)}
            placeholder={!disabled ? `Enter ${_.capitalize(source)} ID` : ''}
            className={`tw-w-full tw-bg-gray-600 tw-border ${
              item.validated && item.error
                ? 'tw-border-westburyRed'
                : 'tw-border-gray-600'
            } tw-rounded-md tw-px-2 tw-py-1 tw-text-white tw-outline-none tw-text-sm`}
            readOnly={item.validated && item.valid && !item.editing}
          />
        </div>
      </div>
      {/* Error message */}
      {item.error && (
        <p className="tw-text-westburyRed tw-text-xs tw-mt-1">
          ID does not exist
        </p>
      )}
      {shouldShowDuplicateAliasSourceIDError && (
        <p className="tw-text-westburyRed tw-text-xs tw-mt-1">
          Duplicate {sourceName} ID
        </p>
      )}
    </div>
  );
}
